import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HiCalendarDays,
  HiChevronDown,
  HiSquares2X2,
  HiStar,
  HiUser,
  HiXMark,
} from "react-icons/hi2";

// Convert the UI state into the exact query payload expected by the API.
const createApiQueryFromFilters = (filters) => {
  const sortByValue = {
    "created-desc": { sort_column: "created_at", sort_direction: "DESC" },
    "created-asc": { sort_column: "created_at", sort_direction: "ASC" },
    title: { sort_column: "title", sort_direction: "ASC" },
  };

  const selectedArray = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }
    return value ? [value] : [];
  };

  return {
    ...(sortByValue[filters.sort] || sortByValue["created-desc"]),
    search_term: filters.search || "",
    selectedAuthors: selectedArray(filters.author),
    selectedChapters: selectedArray(filters.chapter),
    selectedGrades: selectedArray(filters.grade),
    selectedDifficulties: selectedArray(filters.difficulty),
    selectedLanguages: selectedArray(filters.language),
    selectedTerms: selectedArray(filters.term),
    selectedYears: selectedArray(filters.year),
    selectedsubjects: selectedArray(filters.subject),
    selectedStatuses: selectedArray(filters.status),
    selectedExamCategories: selectedArray(filters.examCategory),
    selectedSources: selectedArray(filters.source),
    selectedCreators: selectedArray(filters.createdBy),

    // Used by question library. 
    selectedCategories: selectedArray(filters.category),
    selectedTypes: selectedArray(filters.type),
    selectedTeachers: selectedArray(filters.createdBy),

    selectedStartDate: filters.startDate || null,
    selectedEndDate: filters.endDate || null,
  };
};

const createUiFiltersFromApiQuery = (apiQuery = {}) => {
  const sortValue = `${apiQuery.sort_column || "created_at"}-${apiQuery.sort_direction || "DESC"}`.toLowerCase();
  const sort =
    sortValue === "created_at-asc"
      ? "created-asc"
      : sortValue === "title-asc"
        ? "title"
        : "created-desc";

  return {
    search: apiQuery.search_term || "",
    startDate: apiQuery.selectedStartDate || "",
    endDate: apiQuery.selectedEndDate || "",
    grade: Array.isArray(apiQuery.selectedGrades) ? apiQuery.selectedGrades : [],
    language: Array.isArray(apiQuery.selectedLanguages) ? apiQuery.selectedLanguages : [],
    subject: Array.isArray(apiQuery.selectedsubjects) ? apiQuery.selectedsubjects : [],
    term: Array.isArray(apiQuery.selectedTerms) ? apiQuery.selectedTerms : [],
    author: Array.isArray(apiQuery.selectedAuthors) ? apiQuery.selectedAuthors : [],
    chapter: Array.isArray(apiQuery.selectedChapters) ? apiQuery.selectedChapters : [],
    difficulty: Array.isArray(apiQuery.selectedDifficulties) ? apiQuery.selectedDifficulties : [],
    status: Array.isArray(apiQuery.selectedStatuses) ? apiQuery.selectedStatuses : [],
    examCategory: Array.isArray(apiQuery.selectedExamCategories) ? apiQuery.selectedExamCategories : [],

    // Question Library uses selectedCategories and selectedTypes 
    category: Array.isArray(apiQuery.selectedCategories) ? apiQuery.selectedCategories : [],
    type: Array.isArray(apiQuery.selectedTypes) ? apiQuery.selectedTypes : [],
    createdBy:
      Array.isArray(apiQuery.selectedTeachers) && apiQuery.selectedTeachers.length > 0
        ? apiQuery.selectedTeachers
        : Array.isArray(apiQuery.selectedCreators)
          ? apiQuery.selectedCreators
          : [],


    source: Array.isArray(apiQuery.selectedSources) ? apiQuery.selectedSources : [],
    // createdBy: Array.isArray(apiQuery.selectedCreators) ? apiQuery.selectedCreators : [],
    year: Array.isArray(apiQuery.selectedYears) ? apiQuery.selectedYears : [],
    sort,
  };
};

export default function Filter({
  metaDataOptions = {},
  onQueryChange = () => {},
  apiFilters = {},
  syncKey = "default",
  count = 0,
  countLabel = "Number of Assessments",
  searchAriaLabel = "Search assessments",
  panelFirst = true,

  //allows to reuse this Filter UI
  filterConfigOverride = null,
  // allows pages to add extra initial filter 
  extraInitialFilters = {},
}) {
  // Local UI state mirrors the API filter shape used by the parent page.
  const initialFilters = {
    search: "",
    startDate: "",
    endDate: "",
    grade: [],
    language: [],
    subject: [],
    term: [],
    author: [],
    chapter: [],
    difficulty: [],
    status: [],
    examCategory: [],
    source: [],
    createdBy: [],
    year: [],
    sort: "created-desc",
    ...extraInitialFilters,
  };
  const [filters, setFilters] = useState(() => ({
    ...initialFilters,
    ...createUiFiltersFromApiQuery(apiFilters),
  }));
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const didMountRef = useRef(false);
  const suppressNextEmitRef = useRef(false);
  const onQueryChangeRef = useRef(onQueryChange);
  const rootRef = useRef(null);
  // console.log("MetaDataOptions in Filter:", metaDataOptions); // Debug log to check if options are received correctly
  //----------------------------------------------------------
  // Handlers and memoized values meta data
  //----------------------------------------------------------
  const defaultFilterConfig = [
    { key: "grade", label: "Grade", icon: HiStar, optionKey: "gradeOptions" },
    { key: "language", label: "Language", icon: HiSquares2X2, optionKey: "languageOptions" },
    { key: "subject", label: "Subject", icon: HiSquares2X2, optionKey: "subjectOptions" },
    { key: "term", label: "Term", icon: HiSquares2X2, optionKey: "termOptions" },
    { key: "author", label: "Author", icon: HiSquares2X2, optionKey: "authorOptions" },
    { key: "chapter", label: "Chapter", icon: HiSquares2X2, optionKey: "chapterOptions" },
    { key: "difficulty", label: "Difficulty", icon: HiSquares2X2, optionKey: "difficultyOptions" },
    { key: "status", label: "Status", icon: HiSquares2X2, optionKey: "StatusOptions" },
    { key: "examCategory", label: "Exam Category", icon: HiSquares2X2, optionKey: "examCategoryOptions" },
    { key: "source", label: "Source", icon: HiSquares2X2, optionKey: "sourceOptions" },
    { key: "createdBy", label: "Created By", icon: HiUser, optionKey: "createdByOptions" },
    { key: "year", label: "Year", icon: HiCalendarDays, optionKey: "yearOptions" },

  ];

  // Use the default assessment filters unless a page provides its own chip list.
  const filterConfig = filterConfigOverride || defaultFilterConfig;

  const options = useMemo(() => {
    return filterConfig.reduce((acc, filter) => {
      acc[filter.key] = metaDataOptions?.[filter.optionKey] || [];
      return acc;
    }, {});
  }, [metaDataOptions,filterConfig]);

  const getSelectedValues = (key) => (Array.isArray(filters[key]) ? filters[key] : []);

  // Show a compact summary in the chip trigger instead of listing every selected value.
  const getSelectedLabel = (filter) => {
    const selectedValues = getSelectedValues(filter.key);
    if (!selectedValues.length) {
      return filter.label;
    }

    const selectedOptions = (options[filter.key] || []).filter((option) =>
      selectedValues.includes(option.value)
    );

    if (!selectedOptions.length) {
      return `${filter.label} (${selectedValues.length})`;
    }

    const firstLabel = selectedOptions[0].label;
    return selectedOptions.length > 1
      ? `${filter.label} (${firstLabel} +${selectedOptions.length - 1})`
      : `${filter.label} (${firstLabel})`;
  };

  const openFilterKeyRef = useRef(openFilterKey);
  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);

  useEffect(() => {
    // Close any open menu when the user clicks outside the active filter chip or presses Escape.
    const handleOutsideClick = (event) => {
      const openKey = openFilterKeyRef.current;
      if (!openKey) return;

      const activeChipEl = rootRef.current?.querySelector(`.assessment-filter-chip[data-filter-key="${openKey}"]`);
      if (activeChipEl && !activeChipEl.contains(event.target)) {
        setOpenFilterKey(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenFilterKey(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  }, [onQueryChange]);

  useEffect(() => {
    setFilters({
      ...initialFilters,
      ...createUiFiltersFromApiQuery(apiFilters),
    });
    suppressNextEmitRef.current = true;
    setOpenFilterKey(null);
  }, [syncKey]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return undefined;
    }

    if (suppressNextEmitRef.current) {
      suppressNextEmitRef.current = false;
      return undefined;
    }

    const timerId = setTimeout(() => {
      onQueryChangeRef.current(createApiQueryFromFilters(filters));
    }, 300);

    return () => clearTimeout(timerId);
  }, [filters]);

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const toggleMultiSelectValue = (key, value) => {
    // Multi-select values are stored as arrays and toggled one item at a time.
    setFilters((prev) => {
      const currentValues = Array.isArray(prev[key]) ? prev[key] : [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];

      return { ...prev, [key]: nextValues };
    });
  };

  const clearFilters = () => {
    suppressNextEmitRef.current = true;
    setFilters(initialFilters);
    setOpenFilterKey(null);
    onQueryChangeRef.current(createApiQueryFromFilters(initialFilters));
  };

  const topControls = (
    <div className="assessment-filter__top row g-2 align-items-center justify-content-end">
      <div className="col-12 col-lg-4">
        <div className="d-flex gap-2">
          <input
            type="search"
            placeholder="Search"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                updateFilter("search", "");
              }
            }}
            aria-label={searchAriaLabel}
          />
        </div>
      </div>
      <div className="col-12 col-md-5 col-lg-auto">
        <div className="assessment-filter__count">
          {countLabel} <strong>{count}</strong>
        </div>
      </div>
      <div className="col-12 col-md-7 col-lg-auto">
        <label className="assessment-filter__sort">
          <span>Sort By:</span>
          <select
            value={filters.sort}
            onChange={(e) => updateFilter("sort", e.target.value)}
            aria-label="Sort assessments"
          >
            <option value="created-desc">Created Date</option>
            <option value="created-asc">Oldest Date</option>
            <option value="title">Title</option>
          </select>
        </label>
      </div>
    </div>
  );

  const filterPanel = (
    <div className="assessment-filter__panel">
      {/* Date range is always applied alongside the chip filters. */}
      <div className="assessment-filter__dates row g-2">
        <label className="col-12 col-md-auto">
          <span>Start Date:</span>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => updateFilter("startDate", e.target.value)}
          />
        </label>
        <label className="col-12 col-md-auto">
          <span>End Date:</span>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => updateFilter("endDate", e.target.value)}
          />
        </label>
      </div>

      <div className="assessment-filter__chips">
        {filterConfig.map((filter) => {
          const Icon = filter.icon;
          const isOpen = openFilterKey === filter.key;
          const isActive = getSelectedValues(filter.key).length > 0;
          return (
            <div
              className={`assessment-filter-chip assessment-filter-chip--multi ${isActive ? "active" : ""}`}
              key={filter.key}
              data-filter-key={filter.key}
            >
              {/* The chip opens a checkbox menu so each filter can hold multiple values. */}
              <button
                type="button"
                className={`assessment-filter-chip__trigger ${isActive ? "active" : ""} ${isOpen ? "open" : ""}`}
                onClick={() => setOpenFilterKey((prev) => (prev === filter.key ? null : filter.key))}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={filter.label}
              >
                <Icon aria-hidden="true" />
                <span className="assessment-filter-chip__label">{getSelectedLabel(filter)}</span>
                <HiChevronDown aria-hidden="true" />
              </button>

              {isOpen && (
                <div className="assessment-filter-chip__menu" role="menu" aria-label={filter.label}>
                  <div className="assessment-filter-chip__menu-body">
                    {(options[filter.key] || []).length ? (
                      (options[filter.key] || []).map((option) => {
                        const checked = getSelectedValues(filter.key).includes(option.value);
                        return (
                          <label className="assessment-filter-chip__option" key={option.value}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMultiSelectValue(filter.key, option.value)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="assessment-filter-chip__empty">No options available</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          className="assessment-filter-btn assessment-filter-btn--clear assessment-filter__clear-right"
          onClick={clearFilters}
        >
          <HiXMark aria-hidden="true" />
          Clear Filters
        </button>
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`assessment-filter ${panelFirst ? "" : "assessment-filter--top-first"}`}
    >
      {panelFirst ? filterPanel : topControls}
      {panelFirst ? topControls : filterPanel}
    </div>
  );
}
